'use strict';

import { Entity } from '../Entity';
import { User } from '../User/User';
import { Utilities } from '../../Utilities/Utilities';
import type {
  CommissionStatementMappingData,
  CommissionStatementMappingDuplicateType,
  CommissionStatementMappingFieldName,
} from '../../Repositories/CommissionStatement/CommissionStatementMappingRepository';
import { CommissionStatementMappingCompanyCollection } from '../../Collections/CommissionStatement/CommissionStatementMappingCompanyCollection';
import { CommissionStatementMappingCompany } from './CommissionStatementMappingCompany';

/**
 * Commission Statement Mapping Entity
 *
 * Represents a mapping configuration that defines how to parse and interpret
 * commission statement files from different insurance providers. This entity
 * contains the field mappings, company mappings, and parsing rules needed
 * to transform raw commission data into structured information.
 */
export class CommissionStatementMapping extends Entity {
  /** Unique identifier for the mapping */
  protected id: number;

  /** Name of the insurance provider this mapping is for */
  protected providerName: string;

  /** Type of duplicate handling strategy for this mapping */
  protected duplicateType: CommissionStatementMappingDuplicateType;

  /** Parent mapping if this is derived from another mapping */
  protected parent: CommissionStatementMapping;

  /** Collection of company mappings for this statement mapping */
  protected commissionStatementMappingCompanies: CommissionStatementMappingCompanyCollection;

  /** User who owns this mapping */
  protected user: User;

  /** Date and time when the mapping was created */
  protected dateTimeCreated: Date;

  /** Array of field mappings defining column-to-field relationships */
  protected data: Array<CommissionStatementMappingData> | null;

  /** Whether this mapping is shared with other users */
  protected isShared: boolean;

  /** Array of row numbers to ignore during processing */
  protected ignoredRows: Array<number> | null;

  /** Row number containing column headers */
  protected headerRow: number | null;

  /**
   * Constructor
   *
   * Initializes a new CommissionStatementMapping instance with
   * an empty collection of company mappings.
   */
  constructor() {
    super();
    this.commissionStatementMappingCompanies = new CommissionStatementMappingCompanyCollection();
  }

  /**
   * Hydrate the entity with data from API response
   *
   * Populates the mapping entity with data from the API, including
   * related entities like user, parent mapping, and company mappings.
   *
   * @param data - Raw data object from API
   */
  public hydrate(data: any) {
    // Basic properties
    this.id = data.id;
    this.providerName = data.providerName;
    this.data = data.data;
    this.duplicateType = data.duplicateType;
    this.isShared = data.isShared;
    this.headerRow = data.headerRow;
    this.ignoredRows = data.ignoredRows;

    // Hydrate related User entity
    if (data.user) {
      const user = new User();
      user.hydrate(data.user);
      this.user = user;
    }

    // Hydrate parent mapping if this is a derived mapping
    if (data.parent) {
      const parent = new CommissionStatementMapping();
      parent.hydrate(data.parent);
      this.parent = parent;
    }

    // Hydrate company mappings collection
    if (data.commissionStatementMappingCompanies) {
      data.commissionStatementMappingCompanies.forEach((commissionStatementMappingCompanyData) => {
        const commissionStatementMappingCompany = new CommissionStatementMappingCompany();
        commissionStatementMappingCompany.hydrate(commissionStatementMappingCompanyData);
        this.commissionStatementMappingCompanies.addItem(commissionStatementMappingCompany);
      });
    }

    // Convert date string to Date object
    if (data.dateTimeCreated) {
      const dateTimeCreated = Utilities.dateTimeStringToDateTime(data.dateTimeCreated);
      this.dateTimeCreated = dateTimeCreated;
    }
  }

  // Getter and Setter Methods

  /**
   * Get the mapping ID
   *
   * @returns The unique identifier
   */
  public getId(): number {
    return this.id;
  }

  /**
   * Get the duplicate handling type
   *
   * @returns The duplicate handling strategy for this mapping
   */
  public getDuplicateType(): CommissionStatementMappingDuplicateType {
    return this.duplicateType;
  }

  /**
   * Set the duplicate handling type
   *
   * @param duplicateType - The duplicate handling strategy to use
   * @returns This instance for method chaining
   */
  public setDuplicateType(duplicateType: CommissionStatementMappingDuplicateType): this {
    this.duplicateType = duplicateType;
    return this;
  }

  /**
   * Get the provider name
   *
   * @returns The name of the insurance provider
   */
  public getProviderName(): string {
    return this.providerName;
  }

  /**
   * Set the provider name
   *
   * @param providerName - The name of the insurance provider
   * @returns This instance for method chaining
   */
  public setProviderName(providerName: string): this {
    this.providerName = providerName;
    return this;
  }

  /**
   * Get the field mapping data
   *
   * @returns Array of field mappings or null if not set
   */
  public getData(): Array<CommissionStatementMappingData> | null {
    return this.data;
  }

  /**
   * Set the field mapping data
   *
   * @param data - Array of field mappings defining column-to-field relationships
   * @returns This instance for method chaining
   */
  public setData(data: Array<CommissionStatementMappingData> | null): this {
    this.data = data;
    return this;
  }

  /**
   * Get the array of ignored row numbers
   *
   * @returns Array of row numbers to ignore during processing or null
   */
  public getIgnoredRows(): Array<number> | null {
    return this.ignoredRows;
  }

  /**
   * Set the array of ignored row numbers
   *
   * @param ignoredRows - Array of row numbers to ignore during processing
   * @returns This instance for method chaining
   */
  public setIgnoredRows(ignoredRows: Array<number> | null): this {
    this.ignoredRows = ignoredRows;
    return this;
  }

  /**
   * Get the header row number
   *
   * @returns The row number containing column headers
   */
  public getHeaderRow(): number {
    return this.headerRow;
  }

  /**
   * Set the header row number
   *
   * @param headerRow - The row number containing column headers
   * @returns This instance for method chaining
   */
  public setHeaderRow(headerRow: number): this {
    this.headerRow = headerRow;
    return this;
  }

  /**
   * Get whether this mapping is shared
   *
   * @returns True if the mapping is shared with other users, false if private
   */
  public getIsShared(): boolean {
    return this.isShared;
  }

  /**
   * Set whether this mapping is shared
   *
   * @param isShared - True to share with other users, false to keep private
   * @returns This instance for method chaining
   */
  public setIsShared(isShared: boolean): this {
    this.isShared = isShared;
    return this;
  }

  /**
   * Get the associated user
   *
   * @returns The user who owns this mapping
   */
  public getUser(): User {
    return this.user;
  }

  /**
   * Set the associated user
   *
   * @param user - The user who owns this mapping
   * @returns This instance for method chaining
   */
  public setUser(user: User): this {
    this.user = user;
    return this;
  }

  /**
   * Get the parent mapping
   *
   * @returns The parent mapping if this is derived from another mapping
   */
  public getParent(): CommissionStatementMapping {
    return this.parent;
  }

  /**
   * Set the parent mapping
   *
   * @param parent - The parent mapping this one is derived from
   * @returns This instance for method chaining
   */
  public setParent(parent: CommissionStatementMapping): this {
    this.parent = parent;
    return this;
  }

  /**
   * Get the collection of company mappings
   *
   * @returns Collection of company mappings associated with this statement mapping
   */
  public getCommissionStatementMappingCompanies(): CommissionStatementMappingCompanyCollection {
    return this.commissionStatementMappingCompanies;
  }

  /**
   * Set the collection of company mappings
   *
   * @param commissionStatementMappingCompanies - Collection of company mappings
   * @returns This instance for method chaining
   */
  public setCommissionStatementMappingCompanies(
    commissionStatementMappingCompanies: CommissionStatementMappingCompanyCollection
  ): this {
    this.commissionStatementMappingCompanies = commissionStatementMappingCompanies;
    return this;
  }

  /**
   * Get the creation date and time
   *
   * @returns The date and time when this mapping was created
   */
  public getDateTimeCreated(): Date {
    return this.dateTimeCreated;
  }

  /**
   * Set the creation date and time
   *
   * @param dateTimeCreated - The creation date and time
   * @returns This instance for method chaining
   */
  public setDateTimeCreated(dateTimeCreated: Date): this {
    this.dateTimeCreated = dateTimeCreated;
    return this;
  }

  // Utility Methods

  /**
   * Get the column number for a specific field name
   *
   * Searches through the mapping data to find the column index
   * associated with a specific field name. This is used to locate
   * data columns in commission statements during processing.
   *
   * @param name - The field name to search for
   * @returns The column number (0-based index) or null if field is not mapped
   */
  public getColumnByFieldName(name: CommissionStatementMappingFieldName): number | null {
    // Ensure mapping data exists
    if (!this.data) {
      return null;
    }

    // Find the field mapping that matches the requested field name
    const field = this.data.find((dataSingle) => dataSingle.field === name);

    // Return the column index if found, otherwise null
    return field ? field.column : null;
  }
}
